import React, { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import axios from "axios";
import PropTypes from "prop-types";
import fiLocale from "@fullcalendar/core/locales/fi";

const GigCalendar = ({ bandId, onDateSelect, refreshKey }) => {
  const [events, setEvents] = useState([]);

  const handleEventClick = (clickInfo) => {
    const dateStr = clickInfo.event.startStr;
    const status = clickInfo.event.extendedProps?.status || "free";
    const sentAt = clickInfo.event.extendedProps?.sent_at || null;

    if (onDateSelect) {
      onDateSelect(dateStr, status, sentAt);
    }
  };

  useEffect(() => {
    if (!bandId) {
      console.error("Virhe: bandId puuttuu!");
      return;
    }

    axios
      .get(`https://spiderlegsproductions.com/api/get_calendar.php?band_id=${bandId}`)
      .then((res) => {
        if (res.data && Array.isArray(res.data.events)) {
          const now = new Date().getTime();

          const filtered = res.data.events.filter(event => {
            const isPending = event.extendedProps?.status === "pending";
            const sentAt = event.extendedProps?.sent_at;
            if (isPending && sentAt) {
              const expires = new Date(sentAt).getTime() + 24 * 60 * 60 * 1000;
              return expires > now;
            }
            return true;
          });

          setEvents(filtered);
        } else {
          console.error("Virhe: API ei palauttanut keikkadataa oikein", res.data);
        }
      })
      .catch((err) => {
        console.error("Virhe kalenteridatan haussa:", err);
      });
  }, [bandId, refreshKey]);

  // 🛑 Estä menneiden päivien klikkaus
  const handleDateClick = (arg) => {
    const clickedDate = new Date(arg.dateStr);
    const today = new Date(new Date().toDateString());

    if (clickedDate < today) return;

    const clickedDateStr = arg.dateStr;
    const matchingEvent = events.find(event =>
      event.start === clickedDateStr ||
      (event.start <= clickedDateStr && clickedDateStr <= event.end)
    );

    const status = matchingEvent?.extendedProps?.status || "free";
    const sentAt = matchingEvent?.extendedProps?.sent_at || null;

    if (onDateSelect) {
      onDateSelect(clickedDateStr, status, sentAt);
    }
  };

  // 🧾 Näytetään merkinnät (esim. varattu, odottaa)
  const renderEventContent = (eventInfo) => {
    const status = eventInfo.event.extendedProps.status;

    if (status === "booked") {
      return (
        <div className="flex justify-center items-center w-full h-full px-1">
          <span className="text-[11px] font-customFont1 font-semibold text-white bg-pink-500 border border-white rounded-full py-0.5 px-2 shadow-md whitespace-nowrap transition-all hover:scale-110 hover:rotate-1 duration-300 ease-in-out">
            Varattu
          </span>
        </div>
      );
    }

    if (status === "pending") {
      return (
        <div className="flex justify-center items-center w-full h-full px-1">
          <span className="text-[11px] font-customFont1 font-semibold text-black bg-yellow-300 border border-black rounded-full py-0.5 px-2 shadow-md whitespace-nowrap hover:scale-110 hover:rotate-1 transition-transform duration-300 ease-in-out">
            Odottaa
          </span>
        </div>
      );
    }

    return <div>{eventInfo.event.title}</div>;
  };

  // 🕓 Estetään menneisiin päiviin klikkaaminen visuaalisesti
  const dayCellClassNames = (arg) => {
    const cellDate = arg.date;
    const todayMidnight = new Date(new Date().toDateString());
    if (cellDate < todayMidnight) {
      return ["fc-disabled-day"];
    }
    return ["cursor-pointer", "hover:bg-pink-300", "transition-colors"];
  };

  const dayCellDidMount = (info) => {
    const todayMidnight = new Date(new Date().toDateString());
    if (info.date < todayMidnight) {
      info.el.style.pointerEvents = "none";
      info.el.style.opacity = "0.4";
    }
  };

  return (
    <div className="flex flex-col rounded-xl p-4 shadow-xl dark:bg-gradient-to-br from-indigo-800 via-purple-700 to-pink-600">
      <p className="font-customFont1 text-darkest dark:text-white dark:text-shadow-lg text-center xxxsm:text-sm xxsm:text-sm xsm:text-sm text-lg font-light pb-4">
        Valitse haluttu esiintymisen ajankohta kalenterista klikkaamalla vapaata päivää niin yhteydenottolomake aktivoituu. Bookatut päivät on merkitty tekstillä <strong>varattu</strong>.
      </p>

      <FullCalendar
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        events={events}
        locale={fiLocale}
        dateClick={handleDateClick}
        eventClick={handleEventClick}
        eventContent={renderEventContent}
        dayCellClassNames={dayCellClassNames}
        dayCellDidMount={dayCellDidMount}
      />
    </div>
  );
};

GigCalendar.propTypes = {
  bandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onDateSelect: PropTypes.func,
  refreshKey: PropTypes.number,
};

export default GigCalendar;
